import usePart, { partProps, type PartProps, getter, setter } from "../Part";
import {injectExecute, injectFindCategory, injectFindList} from "../PartInjectKeys";
import {computed, type Ref, watch} from "vue";
import type {Cast, FormEditor} from "@t/fd/editor/FormEditor";
import type {ListItemBase} from "@t/list/List";
import {parseDate} from "@t/util/DateUtil";
import {SafeHtml} from "@t/util/SafeHtml";

export function createScreenToDataFunction(cast: Cast | undefined) {
	if (cast) {
		switch (cast) {
			case "string":
				return (value: any) => {
					return value == null ? "" : value.toString();
				};
			case "number":
				return (value: any) => {
					if (typeof value === "number") return value;
					return value == null ? 0 : parseFloat(value.toString());
				};
			case "integer":
				return (value: any) => {
					if (typeof value === "number") return Math.floor(value);
					return value == null ? 0 : parseInt(value.toString());
				};
			case "boolean":
				return (value: any) => {
					return !!value;
				};
		}
	}
	return (value: any) => value;
}

export function cast(c:Cast, value:any):any {
	if (c) {
		switch (c) {
			case "string":
				return value == null ? "" : value.toString();
			case "html":
				return new SafeHtml(value == null ? "" : value.toString());
			case "date":
				return parseDate(value == null ? "" : value.toString());
			case "number":
				if (value == null) return 0;
				if (typeof value === "number") return value;
				return parseFloat(value.toString());
			case "integer":
				if (value == null) return 0;
				if (typeof value === "number") return Math.floor(value);
				return parseInt(value.toString());
			case "boolean":
				return !!value;
		}
	}
	return value;
}

export function createDataToScreenFunction(cast: Cast | undefined) {
	if (cast) {
		switch (cast) {
			case "string":
				return (value: any) => {
					return value == null ? "" : value.toString();
				};
			case "number":
				return (value: any) => {
					if (value == null) return "";
					if (typeof value === "number") {
						if (Number.isNaN(value)) return "";
						return value.toString();
					}
					return "0";
				};
			case "integer":
				return (value: any) => {
					if (value == null) return "";
					if (typeof value === "number") {
						if (Number.isNaN(value)) return "";
						return Math.floor(value).toString();
					}
					return parseInt(value.toString()).toString();
				};
			case "boolean":
				return (value: any) => {
					return !!value; // may be problem with this
				};
		}
	}
	return (value: any) => value;
}

export interface EditorProps extends PartProps {
	part: FormEditor;
}

export const editorProps = {
	...partProps,
};

export default function (props: EditorProps) {
	const partStuff = usePart(props);

	const screenToData = createScreenToDataFunction(props.part.cast);
	const dataToScreen = createDataToScreenFunction(props.part.cast);

	const findList = injectFindList();
	const findCategory = injectFindCategory();
	const execute = injectExecute();

	const list = computed<string|undefined>(() => {
        // @ts-ignore
		return props.part.list;
	});

    interface DataObject {
        [key: string]: any;
    }

	const rawValue = computed(() => {
		if (partStuff.prop.value != null) return getter(partStuff.localData.value as FormData, partStuff.prop.value, partStuff.formData);
		return partStuff.localData.value;
	});

	function rawValueOfProperty(propName:string|number):any {
		return getter(partStuff.localData.value as FormData, propName, partStuff.formData);
	}

	const value = computed({
		get() {
			return dataToScreen(rawValue.value);
		},
		set(v: any) {
            if (partStuff.prop.value != null) setter(partStuff.localData.value as FormData, partStuff.prop.value, screenToData(v), partStuff.formData);
		}
	});

	function getValueOfProperty(propName:string|number):any {
		return dataToScreen(rawValueOfProperty(propName));
	}

	function setValueOfProperty(propName:string|number, value:any):any {
		setter(partStuff.localData.value as FormData, propName, screenToData(value), partStuff.formData);
	}

	const required = computed(() => {
		return !!props.part.required;
	});

	/**
	 * @param offset - add to index in form part
	 * @param force - add tabindex binding even if not present in form part
	 */
	function tabindexBinding(offset:number = 0, force:boolean = false):any {
		const cb:any = {};
		if (typeof props.part.tabindex === "number") cb.tabindex = props.part.tabindex + offset;
		else if (force) cb.tabindex = offset;
		return cb;
	}

	const optional = computed(() => {
		return !required.value;
	});
	const hasList = computed(() => {
		return !!list.value;
	});

	const listItems = computed<ListItemBase[]>(() => {
		let theList;
		if (typeof list.value === "string") return findList(list.value).value; // reference to global list
		else theList = list.value;

		if (typeof theList === "object") {
			if (Array.isArray(theList)) return theList; // the list if directly in part
			// Convert object to list format { name: 'string', value: 'string' }
			const items:ListItemBase[] = [];
			for (const prop in theList as Object) {
				if (Object.prototype.hasOwnProperty.call(theList, prop)) {
                    const n = theList[prop];
                    let name:string;
                    if (typeof n === "string") name = n;
                    else if (typeof n === "number") name = ""+n;
                    else name = "--"

					items.push({ name: name, value: prop });
				}
			}
			return items;
		}
		return []; // empty list
	});
	const bigger = computed(() => {
		return partStuff.option("bigger", false);
	});


    function meta<T>(name: string, initialValue:T):Ref<T> {
        Object.defineProperty(value.value, name, {
            configurable: true,
            enumerable:false,
            value: initialValue,
            writable: true
        });
        return computed<T>({
            get():T {
                return value.value[name];
            },
            set(val:T):void {
                value.value[name] = val;
            }
        });
    }

	const editorStuff = {
		...partStuff,
		screenToData,
		dataToScreen,
		list,
		value,
		rawValueOfProperty,
		getValueOfProperty,
		setValueOfProperty,
		rawValue,
		required,
		optional,
		hasList,
		listItems,
		bigger,
		findList,
		findCategory,
		meta,
		tabindexBinding
	};

	if (props.part.validate) {
		const method = props.part.validate;
		const partClazz = partStuff.clazz;
		editorStuff.clazz = computed(() => {
			let valid = execute(method, partStuff.localData, props.part);
			let current = partClazz.value;
			if (!valid) {
				if (Array.isArray(current)) {
					current = current.concat(["fd-error"]);
				} else current = "fd-error";
			}
			return current;
		});
	}
	if (props.part.watch) {
		const method = props.part.watch;
		watch(() => value.value, (n, o) => {
			execute(method, partStuff.localData, props.part, n, o);
		});
	}
	return editorStuff;
}
